import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useUserRole } from '@/hooks/useUserRole'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  Avatar,
  Button
} from '@/components/ui'
import './UserMenu.css'

/**
 * UserMenu - Dropdown menu dla zalogowanego użytkownika
 * 
 * Zawiera:
 * - Avatar z inicjałami
 * - Link do profilu
 * - Link do zmiany hasła
 * - Przycisk wylogowania
 * 
 * Built with Radix Premium Minimalism design
 */

interface UserMenuProps {
  onMenuClick?: () => void
  isMobile?: boolean
}

const UserMenu: React.FC<UserMenuProps> = ({ onMenuClick, isMobile = false }) => {
  const { user, signOut } = useAuth()
  const { roles } = useUserRole()
  const navigate = useNavigate()
  const [isExpanded, setIsExpanded] = React.useState(false)

  if (!user) return null

  const isModeratorOrAdmin = roles.some(
    (role) => role.role_name === 'moderator' || role.role_name === 'admin'
  )

  // Pobierz inicjały z emaila lub pełnej nazwy
  const getInitials = (): string => {
    if (user.user_metadata?.full_name) {
      const names = user.user_metadata.full_name.split(' ')
      return names.map(n => n[0]).join('').substring(0, 2).toUpperCase()
    }
    // Jeśli nie ma pełnej nazwy, użyj pierwszych 2 liter emaila
    return user.email?.substring(0, 2).toUpperCase() || 'U'
  }

  const handleLogout = async () => {
    await signOut()
    if (onMenuClick) onMenuClick()
    navigate('/')
  }

  const handleMenuItemClick = () => {
    if (onMenuClick) onMenuClick()
  }

  // Mobile version - accordion style menu
  if (isMobile) {
    return (
      <div className="user-menu-mobile">
        <button 
          className="user-menu-mobile-header"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
          aria-label="Menu użytkownika"
        >
          <Avatar
            fallback={getInitials()}
            size="6"
            radius="full"
            variant="solid"
            className="user-menu-avatar"
          />
          <div className="user-menu-mobile-info">
            <div className="user-menu-mobile-name">
              {user.user_metadata?.full_name || 'Użytkownik'}
            </div>
            <div className="user-menu-mobile-email">{user.email}</div>
          </div>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={`user-menu-mobile-chevron ${isExpanded ? 'expanded' : ''}`}
          >
            <path
              d="M4 6L8 10L12 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div className={`user-menu-mobile-content ${isExpanded ? 'expanded' : ''}`}>
          <Link to="/szkolenia/moje" className="user-menu-mobile-link" onClick={handleMenuItemClick}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M5 2.5C5 2.22386 5.22386 2 5.5 2H10.5C10.7761 2 11 2.22386 11 2.5V4H5V2.5Z"
                fill="currentColor"
                opacity="0.9"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M4 4H12C12.5523 4 13 4.44772 13 5V12.5C13 13.3284 12.3284 14 11.5 14H4.5C3.67157 14 3 13.3284 3 12.5V5C3 4.44772 3.44772 4 4 4ZM5 7.25C5 6.83579 5.33579 6.5 5.75 6.5H10.25C10.6642 6.5 11 6.83579 11 7.25C11 7.66421 10.6642 8 10.25 8H5.75C5.33579 8 5 7.66421 5 7.25Z"
                fill="currentColor"
              />
            </svg>
            Moje rezerwacje szkoleń
          </Link>

          {isModeratorOrAdmin && (
            <Link to="/panel-zarzadzania" className="user-menu-mobile-link" onClick={handleMenuItemClick}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M8 1.5L13 4V8C13 11.3 10.9 13.9 8 14.5C5.1 13.9 3 11.3 3 8V4L8 1.5ZM8 3.2L4.5 4.9V8C4.5 10.4 6 12.3 8 12.9C10 12.3 11.5 10.4 11.5 8V4.9L8 3.2Z"
                  fill="currentColor"
                />
              </svg>
              Panel zarządzania
            </Link>
          )}

          <Link to="/profil" className="user-menu-mobile-link" onClick={handleMenuItemClick}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8 8C9.65685 8 11 6.65685 11 5C11 3.34315 9.65685 2 8 2C6.34315 2 5 3.34315 5 5C5 6.65685 6.34315 8 8 8Z"
                fill="currentColor"
              />
              <path
                d="M3 14C3 11.7909 4.79086 10 7 10H9C11.2091 10 13 11.7909 13 14V14H3V14Z"
                fill="currentColor"
              />
            </svg>
            Mój Profil
          </Link>

          <Link to="/zmien-haslo" className="user-menu-mobile-link" onClick={handleMenuItemClick}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8 1C6.067 1 4.5 2.567 4.5 4.5V6H4C3.44772 6 3 6.44772 3 7V13C3 13.5523 3.44772 14 4 14H12C12.5523 14 13 13.5523 13 13V7C13 6.44772 12.5523 6 12 6H11.5V4.5C11.5 2.567 9.933 1 8 1ZM10 6V4.5C10 3.39543 9.10457 2.5 8 2.5C6.89543 2.5 6 3.39543 6 4.5V6H10Z"
                fill="currentColor"
              />
            </svg>
            Zmień hasło
          </Link>

          <button 
            onClick={handleLogout}
            className="user-menu-mobile-logout"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M2 2C2 1.44772 2.44772 1 3 1H9C9.55228 1 10 1.44772 10 2V5H8.5V2.5H3.5V13.5H8.5V11H10V14C10 14.5523 9.55228 15 9 15H3C2.44772 15 2 14.5523 2 14V2ZM10.6464 5.64645L13.2929 8.29289C13.6834 8.68342 13.6834 9.31658 13.2929 9.70711L10.6464 12.3536L9.58579 11.2929L11.1286 9.75H6V8.25H11.1286L9.58579 6.70711L10.6464 5.64645Z"
                fill="currentColor"
              />
            </svg>
            Wyloguj się
          </button>
        </div>
      </div>
    )
  }

  // Desktop version - dropdown menu
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="user-menu-trigger"
          aria-label="Menu użytkownika"
        >
          <Avatar
            fallback={getInitials()}
            size="4"
            radius="full"
            variant="solid"
            className="user-menu-avatar"
          />
          <span className="user-menu-email">{user.email}</span>
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="user-menu-chevron"
          >
            <path
              d="M2.5 4.5L6 8L9.5 4.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="user-menu-content"
        sideOffset={8}
      >
        {/* User Info Header */}
        <DropdownMenuLabel className="user-menu-header">
          <div className="user-menu-header-content">
            <Avatar
              fallback={getInitials()}
              size="6"
              radius="full"
              variant="solid"
            />
            <div className="user-menu-header-text">
              <div className="user-menu-name">
                {user.user_metadata?.full_name || 'Użytkownik'}
              </div>
              <div className="user-menu-email-small">{user.email}</div>
            </div>
          </div>
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        {/* Menu Items */}
        <DropdownMenuItem asChild>
          <Link to="/szkolenia/moje" onClick={handleMenuItemClick}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ marginRight: '8px' }}
              aria-hidden="true"
            >
              <path
                d="M5 2.5C5 2.22386 5.22386 2 5.5 2H10.5C10.7761 2 11 2.22386 11 2.5V4H5V2.5Z"
                fill="currentColor"
                opacity="0.9"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M4 4H12C12.5523 4 13 4.44772 13 5V12.5C13 13.3284 12.3284 14 11.5 14H4.5C3.67157 14 3 13.3284 3 12.5V5C3 4.44772 3.44772 4 4 4ZM5 7.25C5 6.83579 5.33579 6.5 5.75 6.5H10.25C10.6642 6.5 11 6.83579 11 7.25C11 7.66421 10.6642 8 10.25 8H5.75C5.33579 8 5 7.66421 5 7.25Z"
                fill="currentColor"
              />
            </svg>
            Moje rezerwacje szkoleń
          </Link>
        </DropdownMenuItem>

        {isModeratorOrAdmin && (
          <DropdownMenuItem asChild>
            <Link to="/panel-zarzadzania" onClick={handleMenuItemClick}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ marginRight: '8px' }}
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M8 1.5L13 4V8C13 11.3 10.9 13.9 8 14.5C5.1 13.9 3 11.3 3 8V4L8 1.5ZM8 3.2L4.5 4.9V8C4.5 10.4 6 12.3 8 12.9C10 12.3 11.5 10.4 11.5 8V4.9L8 3.2Z"
                  fill="currentColor"
                />
              </svg>
              Panel zarządzania
            </Link>
          </DropdownMenuItem>
        )}

        <DropdownMenuItem asChild>
          <Link to="/profil" onClick={handleMenuItemClick}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ marginRight: '8px' }}
            >
              <path
                d="M8 8C9.65685 8 11 6.65685 11 5C11 3.34315 9.65685 2 8 2C6.34315 2 5 3.34315 5 5C5 6.65685 6.34315 8 8 8Z"
                fill="currentColor"
              />
              <path
                d="M3 14C3 11.7909 4.79086 10 7 10H9C11.2091 10 13 11.7909 13 14V14H3V14Z"
                fill="currentColor"
              />
            </svg>
            Mój Profil
          </Link>
        </DropdownMenuItem>

        <DropdownMenuItem asChild>
          <Link to="/zmien-haslo" onClick={handleMenuItemClick}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ marginRight: '8px' }}
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8 1C6.067 1 4.5 2.567 4.5 4.5V6H4C3.44772 6 3 6.44772 3 7V13C3 13.5523 3.44772 14 4 14H12C12.5523 14 13 13.5523 13 13V7C13 6.44772 12.5523 6 12 6H11.5V4.5C11.5 2.567 9.933 1 8 1ZM10 6V4.5C10 3.39543 9.10457 2.5 8 2.5C6.89543 2.5 6 3.39543 6 4.5V6H10Z"
                fill="currentColor"
              />
            </svg>
            Zmień hasło
          </Link>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={handleLogout}
          className="user-menu-logout"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ marginRight: '8px' }}
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M2 2C2 1.44772 2.44772 1 3 1H9C9.55228 1 10 1.44772 10 2V5H8.5V2.5H3.5V13.5H8.5V11H10V14C10 14.5523 9.55228 15 9 15H3C2.44772 15 2 14.5523 2 14V2ZM10.6464 5.64645L13.2929 8.29289C13.6834 8.68342 13.6834 9.31658 13.2929 9.70711L10.6464 12.3536L9.58579 11.2929L11.1286 9.75H6V8.25H11.1286L9.58579 6.70711L10.6464 5.64645Z"
              fill="currentColor"
            />
          </svg>
          Wyloguj się
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default UserMenu

